'use client'

import Image from 'next/image'
import React, { useState } from 'react'

import type { Media, Review } from '@/payload-types'
import { getMediaUrl } from '@/utilities/getMediaUrl'
import { getVideoEmbedSrc } from '@/utilities/videoEmbed'

function isPopulatedMedia(m: unknown): m is Media {
  return typeof m === 'object' && m !== null && 'id' in (m as object)
}

function mediaSrc(m: Media): string {
  const raw = m.url ?? (m.filename ? `/media/${m.filename}` : null)
  return raw ? getMediaUrl(raw) : ''
}

function PlayButton() {
  return (
    <div className="flex h-[100px] w-[100px] items-center justify-center rounded-full border-[5px] border-white  transition-transform duration-200 group-hover:scale-110">
      <svg
        aria-hidden
        className="h-9 w-9 text-white"
        fill="currentColor"
        viewBox="2 3 20 18"
      >
        <path d="M8 5v14l11-7z" />
      </svg>
    </div>
  )
}

export function ReviewVideoPlayer({ review }: { review: Review }) {
  const [playing, setPlaying] = useState(false)

  const coverMedia = isPopulatedMedia(review.videoCover) ? review.videoCover : null
  const coverSrc = coverMedia ? mediaSrc(coverMedia) : null

  const videoMedia = isPopulatedMedia(review.video) ? review.video : null
  const videoSrc = videoMedia ? mediaSrc(videoMedia) : null
  const videoMime = videoMedia?.mimeType ?? 'video/mp4'

  const rawEmbedSrc = !videoSrc ? getVideoEmbedSrc(review.videoUrl ?? '') : null
  const embedSrc = rawEmbedSrc
    ? `${rawEmbedSrc}${rawEmbedSrc.includes('?') ? '&' : '?'}autoplay=1`
    : null

  const hasCover = Boolean(coverSrc)

  // No video at all — show error message
  if (!videoSrc && !rawEmbedSrc) {
    return (
      <p className="rounded-lg border border-border bg-muted/40 px-4 py-8 text-center text-sm text-muted-foreground">
        Add a valid YouTube or Vimeo URL, or upload a video file in the CMS.
      </p>
    )
  }

  // Show cover image with play button until user clicks
  if (hasCover && !playing) {
    return (
      <div
        className="group relative aspect-video w-full cursor-pointer overflow-hidden bg-black shadow-inner"
        onClick={() => setPlaying(true)}
        role="button"
        tabIndex={0}
        aria-label="Play video"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setPlaying(true)
        }}
      >
        <Image
          src={coverSrc!}
          alt={
            coverMedia?.alt ||
            (review.reviewerName
              ? `Video testimonial from ${review.reviewerName} — Craft Home Remodel`
              : 'Client video testimonial — Craft Home Remodel')
          }
          fill
          className="object-cover"
          sizes="(max-width: 768px) 100vw, 50vw"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <PlayButton />
        </div>
      </div>
    )
  }

  // Uploaded video
  if (videoSrc) {
    return (
      <div className="relative aspect-video w-full overflow-hidden bg-black shadow-inner">
        <video
          className="absolute inset-0 h-full w-full object-cover"
          controls
          autoPlay={playing}
          playsInline
          preload="metadata"
          title="Review video"
        >
          <source src={videoSrc} type={videoMime} />
        </video>
      </div>
    )
  }

  // YouTube / Vimeo embed
  return (
    <div className="relative aspect-video w-full overflow-hidden bg-black shadow-inner">
      <iframe
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 h-full w-full"
        src={embedSrc ?? rawEmbedSrc ?? ''}
        title="Review video"
      />
    </div>
  )
}
